import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { CountryDisplay } from '@/components/location';
import {
  Button,
  EmptyState,
  ErrorState,
  FilterBar,
  SearchInput,
  Select,
  SkeletonRows,
  StatusBadge,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type StatusBadgeTone,
} from '@/components/ui';
import { listSyncBranches, useQuery } from '@/data';
import { formatDateTime, formatRelative } from '@/lib/format';

type OnlineFilter = '' | 'ONLINE' | 'STALE' | 'OFFLINE';

function statusTone(status: string): StatusBadgeTone {
  if (status === 'ONLINE') return 'success';
  if (status === 'STALE') return 'warning';
  if (status === 'OFFLINE') return 'danger';
  return 'neutral';
}

function businessPath(businessId: string, businessCode: string | null): string {
  if (businessCode?.startsWith('SM')) return `/supermarkets/${businessId}`;
  return `/pharmacies/${businessId}`;
}

/** Global Production branch sync health (Batch C). Requires sync.read. */
export function SyncStatusPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<OnlineFilter>('');
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursor = cursorStack[cursorStack.length - 1] ?? undefined;
  const pageIndex = cursorStack.length;

  const { data, error, loading, reload } = useQuery(
    () =>
      listSyncBranches({
        q: q.trim() || undefined,
        status: status || undefined,
        limit: 50,
        cursor: cursor || undefined,
      }),
    [q, status, cursor],
  );

  const semantics = data?.semantics;
  const hint = useMemo(() => {
    if (!semantics) return 'ONLINE ≤10m · STALE ≤24h · OFFLINE otherwise / never synced';
    return `ONLINE: ${semantics.ONLINE}. STALE: ${semantics.STALE}. OFFLINE: ${semantics.OFFLINE}.`;
  }, [semantics]);

  const filtersDirty = !!(q.trim() || status);
  const clearFilters = () => {
    setQ('');
    setStatus('');
    setCursorStack([null]);
  };

  return (
    <>
      <PageHeader
        title="Sync Status"
        subtitle="Production branch sync health across all linked tenants"
        actions={
          <Button variant="secondary" leftIcon={<RefreshCw size={16} aria-hidden="true" />} onClick={() => void reload()}>
            Refresh
          </Button>
        }
      />

      <div className="card">
        <FilterBar dirty={filtersDirty} onReset={clearFilters}>
          <SearchInput
            label="Search branches"
            value={q}
            onChange={(v) => {
              setQ(v);
              setCursorStack([null]);
            }}
            placeholder="Search tenant or branch…"
            className="w-full lg:max-w-md"
          />
          <Select
            aria-label="Online status filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as OnlineFilter);
              setCursorStack([null]);
            }}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'ONLINE', label: 'ONLINE' },
              { value: 'STALE', label: 'STALE' },
              { value: 'OFFLINE', label: 'OFFLINE' },
            ]}
            className="w-full sm:w-48"
          />
          <p
            className="flex items-start gap-1.5 text-xs text-ink-muted lg:max-w-xs"
            title={hint}
          >
            <HelpCircle size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Thresholds: <strong className="font-medium text-ink-secondary">ONLINE ≤10m</strong>
              {' · '}
              <strong className="font-medium text-ink-secondary">STALE ≤24h</strong>
              {' · '}
              OFFLINE otherwise / never synced
            </span>
          </p>
        </FilterBar>

        {error && !data ? (
          <ErrorState
            title="Could not load sync status"
            message={error.message}
            action={<Button onClick={() => void reload()}>Retry</Button>}
          />
        ) : loading && !data ? (
          <TableContainer>
            <SkeletonRows rows={8} columns={6} />
          </TableContainer>
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No branches match"
            description={
              filtersDirty
                ? 'No Production branches matched the current filters. Try clearing search or status.'
                : 'No Production branches are available yet, or none are linked to Central Admin businesses.'
            }
          />
        ) : (
          <>
            <TableContainer label="Sync status">
              <Table className="min-w-[820px]">
                <THead>
                  <TR>
                    <TH className="pl-5">Business</TH>
                    <TH>Location</TH>
                    <TH>Branch</TH>
                    <TH>Status</TH>
                    <TH>Last sync</TH>
                    <TH>Items</TH>
                    <TH className="pr-5">Tenant</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.items.map((row) => (
                    <TR key={row.id}>
                      <TD className="pl-5">
                        {row.businessId ? (
                          <Link
                            to={businessPath(row.businessId, row.businessCode)}
                            className="font-medium text-primary hover:underline"
                          >
                            {row.businessName}
                            {row.businessCode ? (
                              <span className="ml-1 font-mono text-xs text-ink-muted">{row.businessCode}</span>
                            ) : null}
                          </Link>
                        ) : (
                          <span className="text-ink-muted">Unlinked</span>
                        )}
                      </TD>
                      <TD>
                        {row.businessCountry ? (
                          <CountryDisplay country={row.businessCountry} />
                        ) : row.businessId ? (
                          <Link
                            to={businessPath(row.businessId, row.businessCode)}
                            className="text-sm text-primary hover:underline"
                          >
                            {row.businessName ?? 'View business'}
                          </Link>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </TD>
                      <TD>
                        <p className="font-medium text-ink">{row.name}</p>
                        <p className="font-mono text-xs text-ink-muted">{row.branchRef}</p>
                      </TD>
                      <TD>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge
                            tone={statusTone(row.estimatedOnlineStatus)}
                            label={row.estimatedOnlineStatus}
                            title={hint}
                          />
                          {!row.isActive && <StatusBadge tone="neutral" label="Inactive" />}
                        </div>
                      </TD>
                      <TD className="text-sm text-ink-secondary">
                        {row.lastSyncedAt ? (
                          <>
                            {formatRelative(row.lastSyncedAt)}
                            <span className="block text-xs text-ink-muted">{formatDateTime(row.lastSyncedAt)}</span>
                          </>
                        ) : (
                          'Never'
                        )}
                      </TD>
                      <TD className="tabular-nums">{row.itemCount}</TD>
                      <TD className="pr-5 text-sm text-ink-secondary">{row.tenantName}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableContainer>

            <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
              <p className="text-xs text-ink-muted">
                Page {pageIndex} · {data.items.length} on this page
                {data.stale ? ' · stale cache' : ''}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={cursorStack.length <= 1}
                  onClick={() => setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!data.nextCursor}
                  onClick={() => {
                    if (data.nextCursor) setCursorStack((s) => [...s, data.nextCursor]);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
